import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { toast } from '@heidi/design-system/toast';
// eslint-disable-next-line @heidi/rules/no-unguarded-apple-billing -- useSubscriptionUpgrade already guards execution internally via isAppleCompliantBuild(); this hook renders no UI
import { useSubscriptionUpgrade } from '@/features/settings/billing/hooks/useSubscriptionUpgrade';
import { openTeamTrialUpgradeRequestModalAtom } from '@/features/trialsAndUpgrades/team-trial-upgrade-request-modal/atoms';
import { trialRecapModalMessages } from '@/features/trialsAndUpgrades/trial-recap-modal/messages';
import { openTrialUpgradeRequestCallbackModalAtom } from '@/features/trialsAndUpgrades/trial-upgrade-request-callback-modal/atoms';
import { useHasTrialUpgradeCallbackRequested } from '@/features/trialsAndUpgrades/trial-upgrade-request-callback-modal/useHasTrialUpgradeCallbackRequested';
import { getReverseTrialCohort } from '@/features/trialsAndUpgrades/utils/getReverseTrialCohort';
import { getPrivacyPolicyLink } from '@/helpers/getPrivacyPolicyLink';
import { getTermsOfUseLink } from '@/helpers/localiseHelper';
import {
  getDisplayedPriceFromPlan,
  getNewProductTierProduct,
  getPlanFromPeriod,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import {
  trackClinicianTrialEndedClick,
  trackClinicianTrialEndedViewed,
} from '@/models/analytics/features/trackClinicianTrialEndedAnalytics';
import {
  trackClinicianTrialEndedTeamUpgradeRequestSubmit,
  trackPracticeTrialEndedTeamUpgradeRequestSubmit,
} from '@/models/analytics/features/trackTeamTrialUpgradeRequestAnalytics';
import {
  CLINICIAN_TRIAL_ENDED_ACTION,
  Period,
  TRIALS_AND_UPGRADES_SCREEN,
} from '@heidi/commerce/contracts';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { Info } from 'lucide-react';
import { createElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { clinicianTrialEndedModalMessages } from '@heidi/commerce';
import { useReverseTrial, useUserBillingInfo, useUserTier } from '@heidi/commerce/data';

export function useClinicianTrialEndedModal(isOpen: boolean, handleClose: () => void) {
  const intl = useIntl();
  const { userTier } = useUserTier();
  const { previousStatusBeforeClinicianTrial } = useReverseTrial();
  const { data: clinicianProducts, catalogueVersion } = useQueryProducts({
    heidi2Catalogue: true,
  });
  const { data: billingInfo } = useUserBillingInfo();
  // Match the user's existing Stripe cadence (monthly vs annual). Falls back to
  // yearly when plan_cycle is still null during post-signup webhook polling.
  const upgradePeriod = billingInfo?.plan_cycle ?? Period.ONE_YEAR;
  const clinicianPriceDisplay = useMemo(() => {
    const clinicianProduct = getNewProductTierProduct(
      SubscriptionTier.CLINICIAN_PRO,
      clinicianProducts
    );
    if (!clinicianProduct) return undefined;
    const plan = getPlanFromPeriod(upgradePeriod, clinicianProduct);
    return getDisplayedPriceFromPlan(plan, 'month', false, intl.locale);
  }, [clinicianProducts, upgradePeriod, intl.locale]);
  const hasCallbackRequested = useHasTrialUpgradeCallbackRequested();
  const openTrialUpgradeRequestCallbackModal = useSetAtom(
    openTrialUpgradeRequestCallbackModalAtom
  );
  const openTeamTrialUpgradeRequestModal = useSetAtom(
    openTeamTrialUpgradeRequestModalAtom
  );
  const {
    hasTeam,
    isTeamAdmin,
    isTeamStripeManaged,
    isLoadingTeamDetails,
    isNonAdminTeamMember,
  } = useCombinedQueriesTeamGeneralInfo();
  const {
    handleUpgrade,
    isPending: isUpgradePending,
    isUpgradeInFlight,
  } = useSubscriptionUpgrade({
    onCheckoutComplete: handleClose,
  });
  const upgradePrimaryRef = useRef<HTMLButtonElement>(null);

  const termsOfUseUrl = getTermsOfUseLink(intl.locale);
  const privacyPolicyUrl = useMemo(
    () => getPrivacyPolicyLink(undefined, intl.locale),
    [intl.locale]
  );

  const wasClinicianPlan =
    getReverseTrialCohort({
      previousStatusBeforeClinicianTrial,
      userTier,
    }) === 'clinician';
  const screenName = wasClinicianPlan
    ? TRIALS_AND_UPGRADES_SCREEN.clinicianTrialEnded
    : TRIALS_AND_UPGRADES_SCREEN.practiceTrialEnded;
  // The team-request submit reports whichever of the two screens this modal is
  // showing, so the cohort decision above picks the emitter too.
  const trackTeamUpgradeRequestSubmitted = wasClinicianPlan
    ? trackClinicianTrialEndedTeamUpgradeRequestSubmit
    : trackPracticeTrialEndedTeamUpgradeRequestSubmit;
  // Non-admins fall into the Practice / Scribe Team branch; their footer
  // shows a single "Request to upgrade" CTA that opens the existing team
  // upgrade request modal — they never see the upgrade-self / remain pair.
  const isNonAdmin = !!isNonAdminTeamMember;
  // Practice admin on a non–Stripe-managed team: route to the contact-sales
  // callback flow instead of the upgrade pair. Both Clinician and Practice
  // admins can hit this branch (GRO-3062) — the cohort decides the variant
  // copy in the footer.
  const showContactSalesCTA =
    !isLoadingTeamDetails && isTeamAdmin === true && !isTeamStripeManaged;
  const TierIcon = wasClinicianPlan ? TierProFilled : TierPracticeFilled;

  const showBackOnPlanToast = useCallback(() => {
    toast({
      title: intl.formatMessage(clinicianTrialEndedModalMessages.backOnPlanToast, {
        planName: intl.formatMessage(
          wasClinicianPlan
            ? clinicianTrialEndedModalMessages.scribePlusPlanName
            : clinicianTrialEndedModalMessages.scribeTeamPlanName
        ),
      }),
      variant: 'outline',
      tone: 'subtle',
      icon: createElement(Info),
    });
  }, [intl, wasClinicianPlan]);

  const closeWithoutUpgrade = useCallback(() => {
    showBackOnPlanToast();
    handleClose();
  }, [handleClose, showBackOnPlanToast]);

  useEffect(() => {
    if (!isOpen) return;
    trackClinicianTrialEndedViewed({ screen_name: screenName });
  }, [isOpen, screenName]);

  const handleUpgradeClinician = useCallback(() => {
    if (isUpgradeInFlight()) {
      return;
    }
    trackClinicianTrialEndedClick({
      screen_name: screenName,
      action: CLINICIAN_TRIAL_ENDED_ACTION.keepClinician,
      label: intl.formatMessage(clinicianTrialEndedModalMessages.upgradeToClinicianCta),
      target_plan: SubscriptionTier.CLINICIAN_PRO,
      period: upgradePeriod,
      ...(userTier !== undefined && { current_plan: userTier }),
    });
    void handleUpgrade(
      upgradePeriod,
      SubscriptionTier.CLINICIAN_PRO,
      catalogueVersion === 'v3'
        ? (getPlanFromPeriod(
            upgradePeriod,
            getNewProductTierProduct(SubscriptionTier.CLINICIAN_PRO, clinicianProducts)
          )?.id ?? null)
        : undefined
    );
  }, [
    catalogueVersion,
    clinicianProducts,
    handleUpgrade,
    intl,
    isUpgradeInFlight,
    screenName,
    upgradePeriod,
    userTier,
  ]);

  const handleUpgradePractice = useCallback(() => {
    if (isUpgradeInFlight()) {
      return;
    }
    trackClinicianTrialEndedClick({
      screen_name: screenName,
      action: CLINICIAN_TRIAL_ENDED_ACTION.keepPractice,
      label: intl.formatMessage(clinicianTrialEndedModalMessages.upgradeToPracticeCta),
      target_plan: SubscriptionTier.CLINICIAN_PRO_PLUS,
      period: upgradePeriod,
      ...(userTier !== undefined && { current_plan: userTier }),
    });
    void handleUpgrade(
      upgradePeriod,
      SubscriptionTier.CLINICIAN_PRO_PLUS,
      catalogueVersion === 'v3'
        ? (getPlanFromPeriod(
            upgradePeriod,
            getNewProductTierProduct(
              SubscriptionTier.CLINICIAN_PRO_PLUS,
              clinicianProducts
            )
          )?.id ?? null)
        : undefined
    );
  }, [
    catalogueVersion,
    clinicianProducts,
    handleUpgrade,
    intl,
    isUpgradeInFlight,
    screenName,
    upgradePeriod,
    userTier,
  ]);

  const handleRequestToUpgrade = useCallback(() => {
    trackClinicianTrialEndedClick({
      screen_name: screenName,
      action: CLINICIAN_TRIAL_ENDED_ACTION.requestToUpgrade,
      label: intl.formatMessage(clinicianTrialEndedModalMessages.requestToUpgradeCta),
      ...(userTier !== undefined && { current_plan: userTier }),
    });
    openTeamTrialUpgradeRequestModal({
      plan: wasClinicianPlan
        ? SubscriptionTier.CLINICIAN_PRO
        : SubscriptionTier.CLINICIAN_PRO_PLUS,
      trackRequestSubmitted: trackTeamUpgradeRequestSubmitted,
    });
  }, [
    intl,
    openTeamTrialUpgradeRequestModal,
    screenName,
    trackTeamUpgradeRequestSubmitted,
    userTier,
    wasClinicianPlan,
  ]);

  const handleRemain = useCallback(() => {
    trackClinicianTrialEndedClick({
      screen_name: screenName,
      action: CLINICIAN_TRIAL_ENDED_ACTION.remain,
      label: intl.formatMessage(
        wasClinicianPlan
          ? clinicianTrialEndedModalMessages.remainOnScribePlusCta
          : clinicianTrialEndedModalMessages.remainOnScribeTeamCta
      ),
      ...(userTier !== undefined && { current_plan: userTier }),
    });
    closeWithoutUpgrade();
  }, [closeWithoutUpgrade, intl, screenName, userTier, wasClinicianPlan]);

  const handlePracticeTalkToUs = useCallback(() => {
    trackClinicianTrialEndedClick({
      screen_name: screenName,
      action: CLINICIAN_TRIAL_ENDED_ACTION.talkToUs,
      label: intl.formatMessage(clinicianTrialEndedModalMessages.talkToUsSecondary),
      ...(userTier !== undefined && { current_plan: userTier }),
    });
    handleClose();
  }, [handleClose, intl, screenName, userTier]);

  const handleContactSalesClick = useCallback(() => {
    if (hasCallbackRequested) return;

    trackClinicianTrialEndedClick({
      screen_name: screenName,
      action: CLINICIAN_TRIAL_ENDED_ACTION.contactSales,
      label: intl.formatMessage(
        wasClinicianPlan
          ? trialRecapModalMessages.talkToUsKeepPlanCtaClinician
          : trialRecapModalMessages.talkToUsKeepPlanCtaPractice
      ),
      ...(userTier !== undefined && { current_plan: userTier }),
    });
    handleClose();
    openTrialUpgradeRequestCallbackModal();
  }, [
    handleClose,
    hasCallbackRequested,
    intl,
    openTrialUpgradeRequestCallbackModal,
    screenName,
    userTier,
    wasClinicianPlan,
  ]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeWithoutUpgrade();
      }
    },
    [closeWithoutUpgrade]
  );

  return {
    upgradePrimaryRef,
    termsOfUseUrl,
    privacyPolicyUrl,
    hasTeam,
    wasClinicianPlan,
    isNonAdmin,
    showContactSalesCTA,
    hasCallbackRequested,
    isUpgradePending,
    TierIcon,
    clinicianPriceDisplay,
    handleOpenChange,
    handleUpgradeClinician,
    handleUpgradePractice,
    handleRequestToUpgrade,
    handleRemain,
    handlePracticeTalkToUs,
    handleContactSalesClick,
  };
}
